import { useState, useCallback, useEffect, useRef } from "react";
import { fetchFolders, uploadPropertyFiles } from "../services/chatService";
import { normalizeFolder, sortByParentPathThenName } from "../utils/resourceUtils";
import {
  ensureExtension,
  getFileSizeError,
  getMimeType,
  MAX_TOTAL_UPLOAD_BYTES,
  readFileAsBase64,
  validateUploadFiles,
} from "../utils/uploadUtils";

let stagedFileIdCounter = 0;
const nextStagedFileId = () => {
  stagedFileIdCounter += 1;
  return `staged-${stagedFileIdCounter}`;
};

export const useFileUpload = (authToken, propertyId, { onUploadSuccess, onUploadUsage } = {}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const previousPropertyIdRef = useRef(propertyId);

  const resetPanelState = useCallback(() => {
    setIsPanelOpen(false);
    setFolders([]);
    setSelectedFolderId(null);
    setStagedFiles([]);
    setFolderLoading(false);
    setFolderError(null);
    setUploading(false);
    setUploadError(null);
  }, []);

  const loadFolders = useCallback(async () => {
    if (!authToken || !propertyId) {
      return;
    }
    setFolderLoading(true);
    setFolderError(null);
    try {
      const data = await fetchFolders(authToken, propertyId);
      const list = sortByParentPathThenName(
        (Array.isArray(data) ? data : []).map(normalizeFolder)
      );
      setFolders(list);
      if (list.length === 1) {
        setSelectedFolderId(list[0].id);
      }
    } catch (error) {
      console.error("Failed to load folders:", error);
      setFolderError("Could not load folders. Please try again.");
    } finally {
      setFolderLoading(false);
    }
  }, [authToken, propertyId]);

  const openPanel = useCallback(() => {
    if (!propertyId) {
      return;
    }
    setIsPanelOpen(true);
    setUploadError(null);
    loadFolders();
  }, [propertyId, loadFolders]);

  const dismissPanel = useCallback(() => {
    setIsPanelOpen(false);
    setUploadError(null);
  }, []);

  const cancelPanel = useCallback(() => {
    resetPanelState();
  }, [resetPanelState]);

  useEffect(() => {
    if (previousPropertyIdRef.current !== propertyId && isPanelOpen) {
      resetPanelState();
    }
    previousPropertyIdRef.current = propertyId;
  }, [propertyId, isPanelOpen, resetPanelState]);

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (incoming.length === 0) {
        return;
      }

      setUploadError(null);
      const oversizedFiles = incoming.filter((file) => getFileSizeError(file.size));
      if (oversizedFiles.length > 0 && typeof onUploadUsage === "function") {
        onUploadUsage(
          oversizedFiles.map((file) => ({ name: file.name, size: file.size })),
          "upload-limit"
        );
      }

      setStagedFiles((prev) => {
        const next = [...prev];
        incoming.forEach((file) => {
          const sizeError = getFileSizeError(file.size);
          next.push({
            id: nextStagedFileId(),
            file,
            originalName: file.name,
            name: file.name,
            size: file.size,
            mimeType: getMimeType(file),
            error: sizeError,
          });
        });
        return next;
      });
    },
    [onUploadUsage]
  );

  const renameFile = useCallback((id, newName) => {
    setStagedFiles((prev) =>
      prev.map((item) => {
        if (item.id !== id) {
          return item;
        }
        return {
          ...item,
          name: ensureExtension(newName, item.originalName),
        };
      })
    );
  }, []);

  const removeStagedFile = useCallback((id) => {
    setStagedFiles((prev) => prev.filter((item) => item.id !== id));
    setUploadError(null);
  }, []);

  const validation = {
    ...validateUploadFiles(stagedFiles),
    maxTotalBytes: MAX_TOTAL_UPLOAD_BYTES,
  };
  const canSubmit =
    Boolean(selectedFolderId) &&
    stagedFiles.length > 0 &&
    validation.valid &&
    !uploading &&
    !folderLoading;

  const submitUpload = useCallback(async () => {
    if (!canSubmit || !authToken || !propertyId) {
      return false;
    }

    setUploading(true);
    setUploadError(null);

    if (typeof onUploadUsage === "function") {
      onUploadUsage(stagedFiles);
    }

    try {
      const payload = await Promise.all(
        stagedFiles.map(async (item) => ({
          name: item.name,
          base64: await readFileAsBase64(item.file),
          mimeType: item.mimeType,
        }))
      );

      await uploadPropertyFiles(authToken, propertyId, selectedFolderId, payload);

      if (typeof onUploadSuccess === "function") {
        await onUploadSuccess();
      }

      resetPanelState();
      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError(error.message || "Upload failed. Please try again.");
      setUploading(false);
      return false;
    }
  }, [
    authToken,
    canSubmit,
    onUploadSuccess,
    onUploadUsage,
    propertyId,
    resetPanelState,
    selectedFolderId,
    stagedFiles,
  ]);

  return {
    isPanelOpen,
    folders,
    selectedFolderId,
    setSelectedFolderId,
    stagedFiles,
    folderLoading,
    folderError,
    uploading,
    uploadError,
    validation,
    canSubmit,
    openPanel,
    dismissPanel,
    cancelPanel,
    loadFolders,
    addFiles,
    renameFile,
    removeStagedFile,
    submitUpload,
    maxTotalBytes: MAX_TOTAL_UPLOAD_BYTES,
  };
};
